/*******************************************************************************
 * Datagrid payment item class. This will manage a child PaymentItem object.
 *
 * @param  src/type        Source object to populate properties, or the type to set
 *                         the item to.
 * @param  [subType]       Payment Item subtype
 * @param  [amount]        Payment amount
 * @param  [processorType] Optional processor type
 *******************************************************************************/
/* @ngInject */ export function runDataGridPaymentItem(Data, SERVER, Tools) {
  Data.DGPaymentItem = function (srcType, subType, amount, processorType) {
    /***************************************************************************
     * Constants
     ***************************************************************************/
    var LIS_CASH_RECEIVED = SERVER.BooksDBConstants.LIS_CASH_RECEIVED;
    var LIS_CASH_RETURNED = SERVER.BooksDBConstants.LIS_CASH_RETURNED;
    var LIS_CHECK_RECEIVED = SERVER.BooksDBConstants.LIS_CHECK_RECEIVED;
    var LIS_CHECK_RETURNED = SERVER.BooksDBConstants.LIS_CHECK_RETURNED;
    var LIS_CREDIT_CARD = SERVER.BooksDBConstants.LIS_CREDIT_CARD;
    var LIS_DEBIT_CARD = SERVER.BooksDBConstants.LIS_DEBIT_CARD;
    var LIS_WEBSTORE = SERVER.BooksDBConstants.LIS_WEBSTORE;
    var LIS_EXT_WEBSTORE = SERVER.BooksDBConstants.LIS_EXT_WEBSTORE;

    var LIT_JOURNAL_CREDIT = SERVER.BooksDBConstants.LIT_JOURNAL_CREDIT;
    var LIT_JOURNAL_DEBIT = SERVER.BooksDBConstants.LIT_JOURNAL_DEBIT;
    var LIT_MONEY_PAID = SERVER.BooksDBConstants.LIT_MONEY_PAID;
    var LIT_RECEIVABLE_CREDIT = SERVER.BooksDBConstants.LIT_RECEIVABLE_CREDIT;
    var LIT_RECEIVABLE_DEBIT = SERVER.BooksDBConstants.LIT_RECEIVABLE_DEBIT;
    var LIT_STUDENT_FUNDS_CREDIT =
      SERVER.BooksDBConstants.LIT_STUDENT_FUNDS_CREDIT;
    var LIT_STUDENT_FUNDS_DEBIT =
      SERVER.BooksDBConstants.LIT_STUDENT_FUNDS_DEBIT;

    /***************************************************************************
     * Properties
     ***************************************************************************/
    /** Instance Reference. */ var mThis = this;
    /** Source PaymentItem. */ var mPaymentItem =
      arguments.length == 1
        ? srcType
        : {
            type: srcType,
            subType: subType || 0,
            amount: Number(amount),
            processorType: processorType
          };

    /** PaymentItem source accessor. */
    mThis.source = function () {
      return mPaymentItem;
    };

    /***************************************************************************
     * Datagrid Property Accessors
     ***************************************************************************/
    Object.defineProperty(mThis, 'typeString', {
      get: function () {
        return mThis.isCash()
          ? 'CASH'
          : mThis.isCard()
            ? 'CARD'
            : mThis.isCheck()
              ? 'CHECK'
              : mThis.isFunds()
                ? 'FUNDS'
                : mThis.isReceivable()
                  ? 'ON ACCOUNT'
                  : mThis.isWeb()
                    ? 'WEB'
                    : mThis.isBalance()
                      ? 'RECORD'
                      : '';
      }
    });

    Object.defineProperty(mThis, 'name', {
      get: function () {
        return mPaymentItem.name || mPaymentItem.memo;
      },
      set: function (value) {
        mPaymentItem.name = value;
      }
    });

    Object.defineProperty(mThis, 'number', {
      get: function () {
        return mPaymentItem.number;
      },
      set: function (value) {
        mPaymentItem.number = value;
      }
    });

    Object.defineProperty(mThis, 'amount', {
      get: function () {
        return (
          (mThis.isReturn() || mThis.isFundCredit() ? -1 : 1) *
          mPaymentItem.amount
        );
      },
      set: function (value) {
        if (mThis.allowAmountEdit) mPaymentItem.amount = value;
      }
    });

    /***************************************************************************
     * Flag functions
     ***************************************************************************/
    mThis.allowDetailEdit = function () {
      return !mThis.isCash() && !mThis.isFunds() && !mThis.isWeb();
    };
    mThis.allowAmountEdit = function () {
      return !mThis.isWeb() && !mThis.isReturn() && !mThis.isCleared();
    };

    /** Utilized with the Delete column template. */
    mThis.hideDelete = function () {
      return (
        mThis.isCleared() ||
        mThis.isWeb() ||
        (mThis.isCard() && mPaymentItem.cardData) || //Actual Card Charge
        mThis.isReceivable() ||
        mPaymentItem.type == LIT_MONEY_PAID
      );
    };

    mThis.isBalance = function () {
      return (
        [LIT_JOURNAL_CREDIT, LIT_JOURNAL_DEBIT].indexOf(mPaymentItem.type) >= 0
      );
    };
    mThis.isCash = function () {
      return (
        [LIS_CASH_RECEIVED, LIS_CASH_RETURNED].indexOf(mPaymentItem.subType) >=
        0
      );
    };
    mThis.isCard = function () {
      return (
        [LIS_CREDIT_CARD, LIS_DEBIT_CARD].indexOf(mPaymentItem.subType) >= 0
      );
    };
    mThis.isCheck = function () {
      return (
        [LIS_CHECK_RECEIVED, LIS_CHECK_RETURNED].indexOf(
          mPaymentItem.subType
        ) >= 0
      );
    };
    mThis.isCleared = function () {
      return !!mPaymentItem.isCleared;
    };
    mThis.isFundCredit = function () {
      return mPaymentItem.type == LIT_STUDENT_FUNDS_CREDIT;
    };
    mThis.isFundDebit = function () {
      return mPaymentItem.type == LIT_STUDENT_FUNDS_DEBIT;
    };
    mThis.isFunds = function () {
      return mThis.isFundCredit() || mThis.isFundDebit();
    };
    mThis.isNew = function () {
      return !mPaymentItem.id;
    };
    mThis.isNSF = function () {
      return !!mPaymentItem.isNSF;
    };
    mThis.isReceivable = function () {
      return (
        [LIT_RECEIVABLE_CREDIT, LIT_RECEIVABLE_DEBIT].indexOf(
          mPaymentItem.type
        ) >= 0
      );
    };
    mThis.isReturn = function () {
      return (
        [LIT_JOURNAL_DEBIT, LIT_MONEY_PAID].indexOf(mPaymentItem.type) >= 0
      );
    };
    mThis.isWeb = function () {
      return (
        [LIS_WEBSTORE, LIS_EXT_WEBSTORE].indexOf(mPaymentItem.subType) >= 0
      );
    };
  };
};
